import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconUserChangeOutline18 as RoleIcon,
	IconUserPlusOutline18 as UserPlus,
} from "nucleo-ui-outline-18"

import type { MembershipRole } from "@productlane/db"
import { Avatar, IconButton, Loader } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"

const ROLES: { value: MembershipRole; label: string }[] = [
	{ value: "ADMIN", label: "Admin" },
	{ value: "USER", label: "Member" },
	{ value: "VIEWER", label: "Viewer" },
]

const roleLabel = (role: MembershipRole) =>
	ROLES.find((entry) => entry.value === role)?.label ?? role

export function WorkspaceMembersMenu({
	workspaceId,
	disabled,
	onImpersonate,
}: {
	workspaceId: string
	disabled: boolean
	onImpersonate: (userId: string) => void
}) {
	const [open, setOpen] = useState(false)
	const utils = api.useUtils()
	const membersQuery = api.analytics.listWorkspaceMembers.useQuery(
		{ workspaceId },
		{ enabled: open, staleTime: 30000 },
	)
	const members = membersQuery.data ?? []

	const updateRole = api.analytics.updateWorkspaceMemberRole.useMutation({
		onSuccess: async (_result, variables) => {
			toast.success({
				title: `Role changed to ${roleLabel(variables.role)}`,
				icon: RoleIcon,
			})
			await utils.analytics.listWorkspaceMembers.invalidate({ workspaceId })
		},
		onError: (err) => {
			const isGuard = err.data?.code === "CONFLICT"
			const show = isGuard ? toast.info : toast.error
			show({ title: err.message, icon: RoleIcon })
		},
	})

	return (
		<DropdownMenu
			open={open}
			onOpenChange={setOpen}
			trigger={
				<IconButton Icon={UserPlus} variant="ghost" disabled={disabled} />
			}
			contentProps={{ side: "bottom", align: "end" }}
			className="w-72"
		>
			<DropdownMenuLabel>Members</DropdownMenuLabel>
			<DropdownMenuGroup>
				{membersQuery.isLoading ? (
					<div className="flex items-center justify-center py-3">
						<Loader />
					</div>
				) : members.length === 0 ? (
					<div className="px-2 py-2 text-sm text-tertiary">No members</div>
				) : (
					members.map((member) => (
						<DropdownMenuSub key={member.id}>
							<DropdownMenuSubTrigger className="rounded-md p-1 px-2 py-1.5">
								<Avatar
									size="xs"
									name={member.name || member.email}
									image={member.imageUrl}
									className="mr-2 flex-none"
								/>
								<div className="flex min-w-0 flex-1 items-center justify-between gap-2">
									<div className="min-w-0 truncate">
										<div className="truncate text-sm font-medium leading-tight">
											{member.name || member.email}
										</div>
										{member.name ? (
											<div className="truncate text-xs font-normal text-tertiary">
												{member.email}
											</div>
										) : null}
									</div>
									<div className="flex-none text-xs font-normal text-tertiary">
										{roleLabel(member.role)}
									</div>
								</div>
							</DropdownMenuSubTrigger>
							<DropdownMenuSubContent className="w-44">
								<DropdownMenuItem
									Icon={UserPlus}
									onClick={() => {
										onImpersonate(member.id)
									}}
								>
									Impersonate
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuLabel className="text-xs">Role</DropdownMenuLabel>
								{ROLES.map((role) => (
									<DropdownMenuItem
										key={role.value}
										IconRight={member.role === role.value ? Check : undefined}
										disabled={updateRole.isLoading}
										onClick={() => {
											if (member.role === role.value) {
												return
											}
											updateRole.mutate({
												workspaceId,
												userId: member.id,
												role: role.value,
											})
										}}
									>
										{role.label}
									</DropdownMenuItem>
								))}
							</DropdownMenuSubContent>
						</DropdownMenuSub>
					))
				)}
			</DropdownMenuGroup>
		</DropdownMenu>
	)
}
